/**
 * Graph3D.tsx — Grafo 3D interativo do vault BAM
 *
 * Usa react-force-graph-3d (Three.js) para renderizar a rede de notas.
 * Cada nó tem cor baseada na categoria da nota.
 * Nós "glowing" são destacados quando citados pelo agente RAG.
 */

import { useRef, useCallback, useEffect, useState } from 'react'
import ForceGraph3D, { type ForceGraphMethods } from 'react-force-graph-3d'
import * as THREE from 'three'
import SpriteText from 'three-spritetext'

// Paleta de cores por categoria
export const CATEGORY_COLORS: Record<string, string> = {
  '10-Agencia':     '#FFC107', // Amarelo
  '20-Tecnologia':  '#2196F3', // Azul
  '30-Projetos':    '#9C27B0', // Roxo
  '40-Clientes':    '#FF6B35', // Laranja
  '50-Pessoas':     '#4CAF50', // Verde
  '60-Conhecimento':'#57f5c3', // Ciano
  '70-Operacoes':   '#E91E63', // Rosa
  '99-Meta':        '#607D8B', // Cinza
}

// Normaliza nome de categoria (remove acentos pra match com chaves)
function normalizeCategory(cat: string): string {
  return cat
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, '-')
}

function getCategoryColor(category: string): string {
  const normalized = normalizeCategory(category)
  // Match parcial — ex: "60-Conhecimento" bate com a chave
  for (const [key, color] of Object.entries(CATEGORY_COLORS)) {
    if (normalized.startsWith(key) || key.startsWith(normalized)) return color
  }
  return '#607D8B' // fallback cinza
}

// Helpers de cor
function hexToRgb(hex: string): { r: number; g: number; b: number } {
  const m = hex.replace('#', '')
  const v = m.length === 3
    ? m.split('').map((c) => parseInt(c + c, 16))
    : [parseInt(m.slice(0, 2), 16), parseInt(m.slice(2, 4), 16), parseInt(m.slice(4, 6), 16)]
  return { r: v[0], g: v[1], b: v[2] }
}
function hexToRgba(hex: string, alpha: number): string {
  const { r, g, b } = hexToRgb(hex)
  return `rgba(${r},${g},${b},${alpha})`
}
function mixHexColors(a: string, b: string, alpha: number): string {
  const ca = hexToRgb(a)
  const cb = hexToRgb(b)
  return `rgba(${Math.round((ca.r + cb.r) / 2)},${Math.round((ca.g + cb.g) / 2)},${Math.round((ca.b + cb.b) / 2)},${alpha})`
}

export interface GraphNode {
  id: string
  title: string
  category: string
  path: string
  // react-force-graph adiciona x, y, z em runtime
  x?: number
  y?: number
  z?: number
}

export interface GraphLink {
  source: string
  target: string
  /** Rótulo opcional do link (ex: tipo de relação) */
  label?: string
}

export interface GraphData {
  nodes: GraphNode[]
  links: GraphLink[]
}

interface Graph3DProps {
  data: GraphData
  /** IDs de nós para destacar com glow (citados pelo agente) */
  glowNodeIds?: string[]
  onNodeClick?: (node: GraphNode) => void
  selectedNodeId?: string | null
  /** Força de repulsão entre nós (negativo = afasta). Default: -120 */
  chargeStrength?: number
  /** Força de atração nos links (0..1). Default: 0.6 */
  linkStrength?: number
  /** Distância ideal entre nós conectados. Default: 60 */
  linkDistance?: number
  /** Força do centro (puxa tudo pra origem). Default: 0.05 */
  centerStrength?: number
}

export function Graph3D({
  data,
  glowNodeIds = [],
  onNodeClick,
  selectedNodeId,
  chargeStrength = -120,
  linkStrength = 0.6,
  linkDistance = 60,
  centerStrength = 0.05,
}: Graph3DProps) {
  const graphRef = useRef<ForceGraphMethods | undefined>(undefined)
  const containerRef = useRef<HTMLDivElement | null>(null)
  const [dimensions, setDimensions] = useState({
    width: typeof window !== 'undefined' ? window.innerWidth : 800,
    height: typeof window !== 'undefined' ? window.innerHeight : 600,
  })

  // Atualiza dimensões ao redimensionar a janela ou container
  useEffect(() => {
    if (!containerRef.current) return
    const update = () => {
      const el = containerRef.current
      if (!el) return
      setDimensions({ width: el.clientWidth, height: el.clientHeight })
    }
    update()
    const ro = new ResizeObserver(update)
    ro.observe(containerRef.current)
    window.addEventListener('resize', update)
    return () => {
      ro.disconnect()
      window.removeEventListener('resize', update)
    }
  }, [])

  // Escuta evento customizado "node-glow" disparado pelo ChatGlobal
  useEffect(() => {
    const handler = (e: CustomEvent<{ nodeIds: string[] }>) => {
      // O componente pai deve atualizar glowNodeIds via estado
      // Este listener é para integração futura via evento DOM
      console.debug('[Graph3D] node-glow event:', e.detail.nodeIds)
    }
    window.addEventListener('node-glow', handler as EventListener)
    return () => window.removeEventListener('node-glow', handler as EventListener)
  }, [])

  // Aplica forças ao simulador d3 do force-graph
  useEffect(() => {
    if (!data?.nodes?.length) return
    // Aguarda o force-graph montar antes de tocar nas forças
    const t = setTimeout(() => {
      try {
        const g = graphRef.current as unknown as {
          d3Force?: (name: string, force?: unknown) => { strength?: (s: number) => unknown; distance?: (d: number) => unknown; distanceMax?: (d: number) => unknown } | null
          d3ReheatSimulation?: () => void
        } | undefined
        if (!g?.d3Force) return
        const chargeForce = g.d3Force('charge')
        if (chargeForce?.strength) chargeForce.strength(chargeStrength)
        if (chargeForce?.distanceMax) chargeForce.distanceMax(400)
        const linkForce = g.d3Force('link')
        if (linkForce?.strength) linkForce.strength(linkStrength)
        if (linkForce?.distance) linkForce.distance(linkDistance)
        const centerForce = g.d3Force('center')
        if (centerForce?.strength) centerForce.strength(centerStrength)
        g.d3ReheatSimulation?.()
      } catch (e) {
        console.warn('[Graph3D] Erro aplicando forças:', e)
      }
    }, 100)
    return () => clearTimeout(t)
  }, [chargeStrength, linkStrength, linkDistance, centerStrength, data])

  // Foca câmera no nó selecionado E move o ponto central de rotação
  useEffect(() => {
    if (!selectedNodeId || !graphRef.current) return
    const node = data.nodes.find((n) => n.id === selectedNodeId)
    if (node?.x === undefined || node?.y === undefined || node?.z === undefined) return

    const g = graphRef.current as unknown as {
      cameraPosition: (pos: { x: number; y: number; z: number }, lookAt: { x: number; y: number; z: number }, ms: number) => void
      controls: () => { target: { set: (x: number, y: number, z: number) => void }; update: () => void }
    }

    const distance = 80
    const distRatio = 1 + distance / Math.hypot(node.x, node.y, node.z || 1)

    g.cameraPosition(
      { x: node.x * distRatio, y: node.y * distRatio, z: (node.z || 0) * distRatio },
      { x: node.x, y: node.y, z: node.z || 0 },
      800
    )

    // Move o pivot do OrbitControls para o nó — rotação passa a girar em torno dele
    try {
      const controls = g.controls?.()
      if (controls?.target) {
        controls.target.set(node.x, node.y, node.z || 0)
        controls.update?.()
      }
    } catch (e) {
      console.warn('[Graph3D] Erro ajustando controls.target:', e)
    }
  }, [selectedNodeId, data.nodes])

  const glowSet = new Set(glowNodeIds)

  // Cria objeto 3D para cada nó (esfera + rótulo de texto)
  const nodeThreeObject = useCallback(
    (node: object) => {
      const n = node as GraphNode
      const isGlowing = glowSet.has(n.id)
      const isSelected = n.id === selectedNodeId
      const color = getCategoryColor(n.category)

      const group = new THREE.Group()

      // Esfera principal
      const radius = isSelected ? 6 : isGlowing ? 5 : 3.5
      const geometry = new THREE.SphereGeometry(radius)
      const material = new THREE.MeshStandardMaterial({
        color: new THREE.Color(color),
        emissive: new THREE.Color(color),
        emissiveIntensity: isGlowing ? 1.2 : isSelected ? 0.8 : 0.3,
        roughness: 0.3,
        metalness: 0.4,
      })
      const sphere = new THREE.Mesh(geometry, material)
      group.add(sphere)

      // Halo de glow (esfera maior, semi-transparente)
      if (isGlowing || isSelected) {
        const haloGeo = new THREE.SphereGeometry(isSelected ? 10 : 8)
        const haloMat = new THREE.MeshBasicMaterial({
          color: new THREE.Color(color),
          transparent: true,
          opacity: 0.15,
          side: THREE.BackSide,
        })
        group.add(new THREE.Mesh(haloGeo, haloMat))
      }

      // Rótulo de texto (sprite billboard) — igual ao Obsidian Graph View
      const label = new SpriteText(n.title)
      label.color = isSelected || isGlowing ? '#ffffff' : '#cfd0e0'
      label.backgroundColor = 'rgba(8,8,16,0.55)'
      label.padding = 2
      label.borderRadius = 3
      label.textHeight = isSelected ? 5 : isGlowing ? 4.5 : 3.5
      label.fontFace = 'Inter, system-ui, sans-serif'
      label.fontWeight = isSelected || isGlowing ? '600' : '400'
      label.position.set(0, radius + 4, 0)
      group.add(label)

      return group
    },
    [glowNodeIds, selectedNodeId] // eslint-disable-line react-hooks/exhaustive-deps
  )

  // Cria objeto 3D para cada link (sprite de label opcional sobre a linha)
  const linkThreeObject = useCallback((link: object) => {
    const l = link as GraphLink
    if (!l.label) {
      // Retorna grupo vazio invisível — a linha padrão continua sendo desenhada
      // pois usamos linkThreeObjectExtend={true}.
      return new THREE.Group()
    }
    const sprite = new SpriteText(l.label)
    sprite.color = '#9090b0'
    sprite.textHeight = 2.4
    sprite.fontFace = 'Inter, system-ui, sans-serif'
    sprite.backgroundColor = 'rgba(8,8,16,0.4)'
    sprite.padding = 1.5
    sprite.borderRadius = 2
    return sprite
  }, [])

  // Posiciona o sprite de link no meio da aresta (chamado a cada frame)
  const linkPositionUpdate = useCallback(
    (sprite: object, { start, end }: { start: { x: number; y: number; z: number }; end: { x: number; y: number; z: number } }) => {
      if (!sprite) return false
      const obj = sprite as THREE.Object3D
      obj.position.set(
        start.x + (end.x - start.x) / 2,
        start.y + (end.y - start.y) / 2,
        start.z + (end.z - start.z) / 2,
      )
      return true
    },
    [],
  )

  const nodeColor = useCallback(
    (node: object) => getCategoryColor((node as GraphNode).category),
    []
  )

  // Cor do link = mistura das cores dos nós conectados
  const linkColor = useCallback((link: object) => {
    const l = link as { source: string | GraphNode; target: string | GraphNode }
    const src = typeof l.source === 'object' ? l.source : data.nodes.find((n) => n.id === l.source)
    const tgt = typeof l.target === 'object' ? l.target : data.nodes.find((n) => n.id === l.target)
    if (!src || !tgt) return 'rgba(140,140,200,0.55)'
    const cSrc = getCategoryColor(src.category)
    const cTgt = getCategoryColor(tgt.category)
    if (cSrc === cTgt) {
      // Mesma categoria: cor sólida com alpha
      return hexToRgba(cSrc, 0.7)
    }
    // Categorias diferentes: média das cores RGB
    return mixHexColors(cSrc, cTgt, 0.7)
  }, [data.nodes])

  const handleNodeClick = useCallback(
    (node: object) => {
      onNodeClick?.(node as GraphNode)
    },
    [onNodeClick]
  )

  return (
    <div ref={containerRef} style={{ width: '100%', height: '100%', overflow: 'hidden' }}>
      <ForceGraph3D
        ref={graphRef}
        width={dimensions.width}
        height={dimensions.height}
        graphData={data}
        backgroundColor="#080810"
        nodeLabel={(n) => (n as GraphNode).title}
        nodeColor={nodeColor}
        nodeThreeObject={nodeThreeObject}
        nodeThreeObjectExtend={false}
        linkColor={linkColor}
        linkWidth={1.2}
        linkOpacity={0.85}
        linkLabel={(l) => (l as GraphLink).label ?? ''}
        linkThreeObject={linkThreeObject}
        linkThreeObjectExtend={true}
        linkPositionUpdate={linkPositionUpdate}
        linkDirectionalParticles={1}
        linkDirectionalParticleSpeed={0.003}
        linkDirectionalParticleColor={() => 'rgba(87,245,195,0.7)'}
        onNodeClick={handleNodeClick}
        d3AlphaDecay={0.02}
        d3VelocityDecay={0.3}
        enableNodeDrag
        enableNavigationControls
        showNavInfo={false}
      />
    </div>
  )
}
